import { StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { RouterProvider, createRouter } from "@tanstack/react-router";

import { PageLoading } from "@/components/dashboard/page-state";
import Providers from "@/providers";
import { resolveSession } from "@/hooks/shared/session";
import { routeTree } from "@/routeTree.gen";
import "@/styles/globals.css";

/**
 * Code-split routes load a chunk before the next screen can paint. Without a
 * pending component (and with the library default of waiting 1000ms before
 * showing one), a sidebar click looked frozen for seconds. Intent preload warms
 * the chunk on hover so the wait is often already done by click time.
 */
const router = createRouter({
  routeTree,
  defaultPreload: "intent",
  defaultPreloadDelay: 50,
  defaultPendingComponent: PageLoading,
  defaultPendingMs: 0,
  defaultPendingMinMs: 200,
});

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}

async function bootstrap() {
  /* One session read before paint so `/dashboard` can redirect in `beforeLoad`
     without flashing the console shell, and `/login` knows the signed-in state. */
  await resolveSession();

  createRoot(document.getElementById("root")!).render(
    <StrictMode>
      <Providers>
        <RouterProvider router={router} />
      </Providers>
    </StrictMode>
  );
}

void bootstrap();
